import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSensePresenterModes } from "@/api/admin/slide-sense-config"

import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"

import { useSlideSenseConfigQuery, useUpdateSlideSenseConfigMutation } from "../hooks/admin-queries"

/**
 * Admin control for which Slide Hub presenter modes users may launch.
 * Backed by the tenant slide-sense config; both modes default to enabled
 * server-side, so an untouched tenant shows both switches on.
 */
export function SlideHubConfigPanel() {
  const { t } = useTranslation()
  const configQuery = useSlideSenseConfigQuery()
  const update = useUpdateSlideSenseConfigMutation()

  if (configQuery.isLoading) {
    return <Skeleton className="h-24 w-full" />
  }

  if (configQuery.isError) {
    return (
      <p className="text-destructive text-xs">
        {t("adminDashboard.systemConfig.slideHub.loadError", {
          defaultValue: "Could not load Slide Hub settings.",
        })}
      </p>
    )
  }

  const modes = configQuery.data?.presenter_modes ?? {
    presentation: true,
    presenter_view: true,
  }

  const handleToggle = (key: keyof SlideSensePresenterModes, next: boolean) => {
    const previous = modes[key]
    update.mutate(
      { ...modes, [key]: next },
      {
        onError: () => {
          toast.error(
            t("adminDashboard.systemConfig.slideHub.saveError", {
              defaultValue: "Failed to save Slide Hub settings",
            })
          )
        },
        onSuccess: () => {
          if (previous !== next) {
            toast.success(
              t("adminDashboard.systemConfig.slideHub.saved", {
                defaultValue: "Slide Hub settings saved",
              })
            )
          }
        },
      }
    )
  }

  const rows: Array<{
    key: keyof SlideSensePresenterModes
    label: string
    description: string
    testId: string
  }> = [
    {
      key: "presentation",
      label: t("adminDashboard.systemConfig.slideHub.presentationLabel", {
        defaultValue: "Presentation mode",
      }),
      description: t("adminDashboard.systemConfig.slideHub.presentationDescription", {
        defaultValue: "Full-screen audience presentation (the F5-style Present button).",
      }),
      testId: "slide-hub-toggle-presentation",
    },
    {
      key: "presenter_view",
      label: t("adminDashboard.systemConfig.slideHub.presenterViewLabel", {
        defaultValue: "Presenter view",
      }),
      description: t("adminDashboard.systemConfig.slideHub.presenterViewDescription", {
        defaultValue: "Second-window presenter console with speaker notes and next-slide preview.",
      }),
      testId: "slide-hub-toggle-presenter-view",
    },
  ]

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.key} className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium" htmlFor={row.testId}>
              {row.label}
            </Label>
            <p className="text-muted-foreground text-xs">{row.description}</p>
          </div>
          <Switch
            checked={modes[row.key]}
            data-testid={row.testId}
            disabled={update.isPending}
            id={row.testId}
            onCheckedChange={(next) => handleToggle(row.key, next)}
          />
        </div>
      ))}
    </div>
  )
}
